import {
  Field,
  TextInput,
  NumberInput,
  TextArea,
  SelectInput,
  TriToggle,
  TagInput,
  CheckGroup,
  ListEditor,
} from "@/components/assessment/fields";

/* ---------- module-scope option lists (avoid recreating on every render) ---------- */
const OPT_EMPLOYMENT_STATUS = [
  "employed",
  "recently_laid_off",
  "underemployed",
  "looking",
  "unemployed",
  "freelance",
];
const OPT_DEPARTURE_REASON = ["ai_automation", "layoff", "restructuring", "voluntary", "other"];
const OPT_SKILL_CATEGORY = ["technical", "soft", "language", "tool", "domain"];
const OPT_PROFICIENCY = ["beginner", "intermediate", "advanced", "expert"];
const OPT_TEAM_PREF = ["solo", "small_team", "large_team", "mixed"];
const OPT_LOW_MED_HIGH = ["low", "medium", "high"];
const OPT_STRUCTURE_PREF = ["high_structure", "flexible", "autonomous"];
const OPT_CURRENCY = ["USD", "EUR", "GBP", "CAD", "AUD", "INR", "JPY", "BRL", "ZAR", "other"];

/* ---------- shared helpers ---------- */

function Two({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>;
}

/* ============================================================ */
/* 1. Current Situation                                          */
/* ============================================================ */

export function CurrentSituationStep({ data, onChange, errors = {} }) {
  const cs = data.current_situation || {};
  const set = (patch) => onChange({ current_situation: { ...cs, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-current-situation">
      <Field label="Employment status" error={errors.employment_status}>
        <SelectInput
          value={cs.employment_status}
          onChange={(v) => set({ employment_status: v })}
          options={OPT_EMPLOYMENT_STATUS}
          testid="input-employment-status"
        />
      </Field>

      <Two>
        <Field label="Most recent role" optional>
          <TextInput
            value={cs.last_role}
            onChange={(v) => set({ last_role: v })}
            placeholder="e.g. Senior Copy Editor"
            testid="input-last-role"
          />
        </Field>
        <Field label="Most recent industry" optional>
          <TextInput
            value={cs.last_industry}
            onChange={(v) => set({ last_industry: v })}
            placeholder="e.g. Publishing"
            testid="input-last-industry"
          />
        </Field>
      </Two>

      <Two>
        <Field
          label="Months since job change / displacement"
          optional
          hint="Leave blank if you're still in the role."
        >
          <NumberInput
            value={cs.months_since_displacement}
            onChange={(v) => set({ months_since_displacement: v })}
            min={0}
            max={600}
            testid="input-months-since-displacement"
          />
        </Field>
        <Field label="Reason your role changed" optional>
          <SelectInput
            value={cs.displacement_reason}
            onChange={(v) => set({ displacement_reason: v })}
            options={OPT_DEPARTURE_REASON}
            testid="input-displacement-reason"
          />
        </Field>
      </Two>

      <Field
        label="In your own words, what happened?"
        optional
        hint="No need to be detailed — a few sentences is plenty."
      >
        <TextArea
          value={cs.description}
          onChange={(v) => set({ description: v })}
          placeholder="What changed at work, and how are you feeling about it?"
          testid="input-current-situation-description"
        />
      </Field>
    </div>
  );
}

/* ============================================================ */
/* 2. Work History & Skills                                      */
/* ============================================================ */

export function WorkHistorySkillsStep({ data, onChange }) {
  return (
    <div className="space-y-10" data-testid="step-work-history-skills">
      <section>
        <h3 className="text-lg font-medium text-slate-900 mb-1">Work history</h3>
        <p className="text-sm text-slate-500 mb-4">
          {"Add roles starting with the most recent. Skip dates you don't remember exactly."}
        </p>
        <ListEditor
          items={data.work_history}
          onChange={(v) => onChange({ work_history: v })}
          addLabel="Add a role"
          newItem={() => ({
            role: "",
            company: "",
            industry: null,
            start_year: null,
            end_year: null,
            responsibilities: null,
            reason_left: null,
          })}
          testid="list-work-history"
          renderRow={(item, update, idx) => (
            <div className="space-y-4 pr-8">
              <Two>
                <Field label="Role">
                  <TextInput
                    value={item.role}
                    onChange={(v) => update({ role: v })}
                    placeholder="Senior Editor"
                    testid={`work-history-${idx}-role`}
                  />
                </Field>
                <Field label="Company" optional>
                  <TextInput
                    value={item.company}
                    onChange={(v) => update({ company: v })}
                    placeholder="Acme Publishing"
                    testid={`work-history-${idx}-company`}
                  />
                </Field>
              </Two>
              <Two>
                <Field label="Industry" optional>
                  <TextInput
                    value={item.industry}
                    onChange={(v) => update({ industry: v })}
                    placeholder="Publishing"
                    testid={`work-history-${idx}-industry`}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Start year" optional>
                    <NumberInput
                      value={item.start_year}
                      onChange={(v) => update({ start_year: v })}
                      min={1950}
                      max={2100}
                      testid={`work-history-${idx}-start-year`}
                    />
                  </Field>
                  <Field label="End year" optional hint="Leave blank if current">
                    <NumberInput
                      value={item.end_year}
                      onChange={(v) => update({ end_year: v })}
                      min={1950}
                      max={2100}
                      testid={`work-history-${idx}-end-year`}
                    />
                  </Field>
                </div>
              </Two>
              <Field label="Main responsibilities" optional>
                <TextArea
                  value={item.responsibilities}
                  onChange={(v) => update({ responsibilities: v })}
                  rows={3}
                  placeholder="What you actually did day-to-day."
                  testid={`work-history-${idx}-responsibilities`}
                />
              </Field>
              <Field label="Why you left" optional>
                <TextInput
                  value={item.reason_left}
                  onChange={(v) => update({ reason_left: v })}
                  placeholder="Layoff, looking for change, etc."
                  testid={`work-history-${idx}-reason-left`}
                />
              </Field>
            </div>
          )}
        />
      </section>

      <section>
        <h3 className="text-lg font-medium text-slate-900 mb-1">Skills</h3>
        <p className="text-sm text-slate-500 mb-4">
          Things you actually do well — soft skills count just as much as tools.
        </p>
        <ListEditor
          items={data.skills}
          onChange={(v) => onChange({ skills: v })}
          addLabel="Add a skill"
          newItem={() => ({ name: "", category: null, proficiency: null, years: null })}
          testid="list-skills"
          renderRow={(item, update, idx) => (
            <div className="space-y-4 pr-8">
              <Field label="Skill">
                <TextInput
                  value={item.name}
                  onChange={(v) => update({ name: v })}
                  placeholder="Copy editing, Excel, mentoring…"
                  testid={`skill-${idx}-name`}
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Category" optional>
                  <SelectInput
                    value={item.category}
                    onChange={(v) => update({ category: v })}
                    options={OPT_SKILL_CATEGORY}
                    testid={`skill-${idx}-category`}
                  />
                </Field>
                <Field label="Proficiency" optional>
                  <SelectInput
                    value={item.proficiency}
                    onChange={(v) => update({ proficiency: v })}
                    options={OPT_PROFICIENCY}
                    testid={`skill-${idx}-proficiency`}
                  />
                </Field>
                <Field label="Years" optional>
                  <NumberInput
                    value={item.years}
                    onChange={(v) => update({ years: v })}
                    min={0}
                    max={60}
                    step={0.5}
                    testid={`skill-${idx}-years`}
                  />
                </Field>
              </div>
            </div>
          )}
        />
      </section>
    </div>
  );
}

/* ============================================================ */
/* 3. Education                                                  */
/* ============================================================ */

export function EducationStep({ data, onChange }) {
  return (
    <div className="space-y-6" data-testid="step-education">
      <p className="text-sm text-slate-500">
        Add any formal or informal education — degrees, certificates, bootcamps, on-the-job training.
      </p>
      <ListEditor
        items={data.education}
        onChange={(v) => onChange({ education: v })}
        addLabel="Add an entry"
        newItem={() => ({ level: null, field: null, institution: null, year_completed: null })}
        testid="list-education"
        renderRow={(item, update, idx) => (
          <div className="space-y-4 pr-8">
            <Two>
              <Field label="Level">
                <SelectInput
                  value={item.level}
                  onChange={(v) => update({ level: v })}
                  options={[
                    "high_school",
                    "associate",
                    "bachelor",
                    "master",
                    "phd",
                    "certificate",
                    "other",
                  ]}
                  testid={`education-${idx}-level`}
                />
              </Field>
              <Field label="Field of study" optional>
                <TextInput
                  value={item.field}
                  onChange={(v) => update({ field: v })}
                  placeholder="English Literature"
                  testid={`education-${idx}-field`}
                />
              </Field>
            </Two>
            <Two>
              <Field label="Institution" optional>
                <TextInput
                  value={item.institution}
                  onChange={(v) => update({ institution: v })}
                  testid={`education-${idx}-institution`}
                />
              </Field>
              <Field label="Year completed" optional>
                <NumberInput
                  value={item.year_completed}
                  onChange={(v) => update({ year_completed: v })}
                  min={1950}
                  max={2100}
                  testid={`education-${idx}-year-completed`}
                />
              </Field>
            </Two>
          </div>
        )}
      />
    </div>
  );
}

/* ============================================================ */
/* 4. Interests & Hobbies                                        */
/* ============================================================ */

export function InterestsHobbiesStep({ data, onChange }) {
  return (
    <div className="space-y-6" data-testid="step-interests-hobbies">
      <Field
        label="Hobbies & interests"
        optional
        hint="What you actually enjoy doing — these often hint at second careers."
      >
        <TagInput
          value={data.hobbies}
          onChange={(v) => onChange({ hobbies: v })}
          placeholder="e.g. gardening, chess, woodworking"
          testid="input-hobbies"
        />
      </Field>
      <Field
        label="Industries you've always been curious about"
        optional
        hint="No commitment — just a wishlist."
      >
        <TagInput
          value={data.career_goals?.industries_of_interest || []}
          onChange={(v) =>
            onChange({
              career_goals: {
                ...(data.career_goals || {}),
                industries_of_interest: v,
              },
            })
          }
          placeholder="e.g. healthcare, education, design"
          testid="input-industries-of-interest"
        />
      </Field>
    </div>
  );
}

/* ============================================================ */
/* 5. Personality & Work Style                                   */
/* ============================================================ */

export function PersonalityStep({ data, onChange }) {
  const p = data.personality || {};
  const set = (patch) => onChange({ personality: { ...p, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-personality">
      <Two>
        <Field label="Preferred work style" optional>
          <SelectInput
            value={p.work_style}
            onChange={(v) => set({ work_style: v })}
            options={OPT_TEAM_PREF}
            testid="input-work-style"
          />
        </Field>
        <Field label="Risk tolerance" optional>
          <SelectInput
            value={p.risk_tolerance}
            onChange={(v) => set({ risk_tolerance: v })}
            options={OPT_LOW_MED_HIGH}
            testid="input-risk-tolerance"
          />
        </Field>
      </Two>
      <Field label="Structure preference" optional>
        <SelectInput
          value={p.structure_preference}
          onChange={(v) => set({ structure_preference: v })}
          options={OPT_STRUCTURE_PREF}
          testid="input-structure-preference"
        />
      </Field>
      <Field
        label="What you're known for"
        optional
        hint="Pick a few. Your strengths — calm under pressure, great writer, organised, etc."
      >
        <TagInput
          value={p.strengths}
          onChange={(v) => set({ strengths: v })}
          placeholder="Add a strength and press Enter"
          testid="input-strengths"
        />
      </Field>
      <Field
        label="What drains your energy"
        optional
        hint="Knowing what to avoid is just as useful."
      >
        <TagInput
          value={p.energy_drains}
          onChange={(v) => set({ energy_drains: v })}
          placeholder="e.g. back-to-back meetings"
          testid="input-energy-drains"
        />
      </Field>
      <Field label="Anything else about how you work best?" optional>
        <TextArea
          value={p.notes}
          onChange={(v) => set({ notes: v })}
          testid="input-personality-notes"
        />
      </Field>
    </div>
  );
}

/* ============================================================ */
/* 6. Physical & Personal Constraints (sensitive)               */
/* ============================================================ */

export function PhysicalConstraintsStep({ data, onChange }) {
  const c = data.physical_constraints || {};
  const set = (patch) => onChange({ physical_constraints: { ...c, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-physical-constraints">
      <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-900">
        {"Everything in this section is optional. Share only what you're comfortable with — it helps us avoid suggesting roles that won't work for you."}
      </div>

      <Field label="Do you have any physical constraints we should consider?" sensitive>
        <TriToggle
          value={c.has_constraints}
          onChange={(v) => set({ has_constraints: v })}
          testid="input-has-constraints"
        />
      </Field>

      <Field label="Mobility / posture notes" sensitive>
        <TextArea
          value={c.mobility_notes}
          onChange={(v) => set({ mobility_notes: v })}
          placeholder="e.g. can't stand for more than 2 hours, chronic back issues"
          testid="input-mobility-notes"
          rows={3}
        />
      </Field>

      <Two>
        <Field label="Maximum lifting (kg)" sensitive>
          <NumberInput
            value={c.can_lift_kg}
            onChange={(v) => set({ can_lift_kg: v })}
            min={0}
            max={200}
            testid="input-can-lift-kg"
          />
        </Field>
        <Field label="Standing tolerance (hours / day)" sensitive>
          <NumberInput
            value={c.standing_tolerance_hours}
            onChange={(v) => set({ standing_tolerance_hours: v })}
            min={0}
            max={24}
            step={0.5}
            testid="input-standing-tolerance-hours"
          />
        </Field>
      </Two>

      <Field label="Do you need workplace accommodations?" sensitive>
        <TriToggle
          value={c.requires_accommodations}
          onChange={(v) => set({ requires_accommodations: v })}
          testid="input-requires-accommodations"
        />
      </Field>

      <Field label="Accommodation notes" sensitive>
        <TextArea
          value={c.accommodation_notes}
          onChange={(v) => set({ accommodation_notes: v })}
          rows={3}
          testid="input-accommodation-notes"
        />
      </Field>
    </div>
  );
}

/* ============================================================ */
/* 7. Salary & Income (sensitive)                               */
/* ============================================================ */

export function SalaryStep({ data, onChange }) {
  const s = data.salary || {};
  const set = (patch) => onChange({ salary: { ...s, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-salary">
      <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-900">
        {"These numbers help us recommend roles that actually fit your needs. Leave blank if you'd rather not say."}
      </div>

      <Two>
        <Field label="Minimum acceptable annual income" sensitive>
          <NumberInput
            value={s.minimum_annual}
            onChange={(v) => set({ minimum_annual: v })}
            min={0}
            step={1000}
            testid="input-minimum-annual"
          />
        </Field>
        <Field label="Target annual income" sensitive>
          <NumberInput
            value={s.target_annual}
            onChange={(v) => set({ target_annual: v })}
            min={0}
            step={1000}
            testid="input-target-annual"
          />
        </Field>
      </Two>

      <Two>
        <Field label="Currency" optional>
          <SelectInput
            value={s.currency || "USD"}
            onChange={(v) => set({ currency: v || "USD" })}
            options={["USD", "EUR", "GBP", "CAD", "AUD", "INR", "JPY", "BRL", "ZAR", "other"]}
            testid="input-currency"
          />
        </Field>
        <Field label="Open to taking less while transitioning?" optional>
          <TriToggle
            value={s.open_to_lower_during_transition}
            onChange={(v) => set({ open_to_lower_during_transition: v })}
            testid="input-open-to-lower"
          />
        </Field>
      </Two>
    </div>
  );
}

/* ============================================================ */
/* 8. Financial Position (sensitive)                            */
/* ============================================================ */

export function FinancialPositionStep({ data, onChange }) {
  const f = data.financial_position || {};
  const set = (patch) => onChange({ financial_position: { ...f, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-financial-position">
      <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-900">
        Sensitive and optional. We only use this to understand how much time you realistically have to retrain.
      </div>
      <Two>
        <Field label="Financial runway (months you could last without income)" sensitive>
          <NumberInput
            value={f.runway_months}
            onChange={(v) => set({ runway_months: v })}
            min={0}
            max={120}
            testid="input-runway-months"
          />
        </Field>
        <Field label="Monthly essential expenses" sensitive>
          <NumberInput
            value={f.monthly_essential_expenses}
            onChange={(v) => set({ monthly_essential_expenses: v })}
            min={0}
            step={50}
            testid="input-monthly-essential-expenses"
          />
        </Field>
      </Two>
      <Two>
        <Field label="People who depend on your income?" sensitive>
          <TriToggle
            value={f.has_dependents}
            onChange={(v) => set({ has_dependents: v })}
            testid="input-has-dependents"
          />
        </Field>
        <Field label="Do you have a savings buffer?" sensitive>
          <TriToggle
            value={f.has_savings_buffer}
            onChange={(v) => set({ has_savings_buffer: v })}
            testid="input-has-savings-buffer"
          />
        </Field>
      </Two>
      <Field label="Are debts a current source of stress?" sensitive>
        <TriToggle
          value={f.has_debt_stress}
          onChange={(v) => set({ has_debt_stress: v })}
          testid="input-has-debt-stress"
        />
      </Field>
      <Field label="Anything else worth knowing?" sensitive>
        <TextArea
          value={f.notes}
          onChange={(v) => set({ notes: v })}
          rows={3}
          testid="input-financial-notes"
        />
      </Field>
    </div>
  );
}

/* ============================================================ */
/* 9. Location, Travel & Relocation                              */
/* ============================================================ */

export function LocationStep({ data, onChange }) {
  const l = data.location_travel_relocation || {};
  const set = (patch) => onChange({ location_travel_relocation: { ...l, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-location">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <Field label="City" optional>
          <TextInput
            value={l.current_city}
            onChange={(v) => set({ current_city: v })}
            testid="input-current-city"
          />
        </Field>
        <Field label="Region / State" optional>
          <TextInput
            value={l.current_region}
            onChange={(v) => set({ current_region: v })}
            testid="input-current-region"
          />
        </Field>
        <Field label="Country" optional>
          <TextInput
            value={l.current_country}
            onChange={(v) => set({ current_country: v })}
            testid="input-current-country"
          />
        </Field>
      </div>

      <Field label="Work arrangements you're open to" optional>
        <div className="flex flex-wrap gap-3">
          {[
            { key: "open_to_remote", label: "Remote" },
            { key: "open_to_hybrid", label: "Hybrid" },
            { key: "open_to_onsite", label: "Onsite" },
            { key: "open_to_relocation", label: "Relocating" },
          ].map((opt) => {
            const active = l[opt.key] === true;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => set({ [opt.key]: !active })}
                className={
                  "px-3.5 py-1.5 text-sm rounded-full border transition-all " +
                  (active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300")
                }
                data-testid={`toggle-${opt.key}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </Field>

      <Two>
        <Field label="Max relocation radius (km)" optional>
          <NumberInput
            value={l.relocation_radius_km}
            onChange={(v) => set({ relocation_radius_km: v })}
            min={0}
            max={20000}
            testid="input-relocation-radius-km"
          />
        </Field>
        <Field label="Travel tolerance (% of time)" optional>
          <NumberInput
            value={l.travel_tolerance_pct}
            onChange={(v) => set({ travel_tolerance_pct: v })}
            min={0}
            max={100}
            testid="input-travel-tolerance-pct"
          />
        </Field>
      </Two>
    </div>
  );
}

/* ============================================================ */
/* 10. Retraining Capacity                                       */
/* ============================================================ */

export function RetrainingStep({ data, onChange }) {
  const r = data.retraining_capacity || {};
  const set = (patch) => onChange({ retraining_capacity: { ...r, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-retraining">
      <Two>
        <Field label="Hours per week you can dedicate to retraining" optional>
          <NumberInput
            value={r.hours_per_week_available}
            onChange={(v) => set({ hours_per_week_available: v })}
            min={0}
            max={80}
            step={0.5}
            testid="input-hours-per-week"
          />
        </Field>
        <Field label="Budget you could spend on courses" optional>
          <NumberInput
            value={r.budget_for_courses}
            onChange={(v) => set({ budget_for_courses: v })}
            min={0}
            step={50}
            testid="input-budget-for-courses"
          />
        </Field>
      </Two>

      <Field label="Formats that work for you" optional>
        <CheckGroup
          value={r.preferred_formats}
          onChange={(v) => set({ preferred_formats: v })}
          options={[
            "online_self_paced",
            "online_cohort",
            "in_person",
            "bootcamp",
            "certification",
            "degree",
          ]}
          testid="input-preferred-formats"
        />
      </Field>

      <Field label="Completed any course in the last 12 months?" optional>
        <TriToggle
          value={r.has_completed_recent_course}
          onChange={(v) => set({ has_completed_recent_course: v })}
          testid="input-has-completed-recent-course"
        />
      </Field>

      <Field label="Anything we should know about how you learn best?" optional>
        <TextArea
          value={r.notes}
          onChange={(v) => set({ notes: v })}
          rows={3}
          testid="input-retraining-notes"
        />
      </Field>
    </div>
  );
}

/* ============================================================ */
/* 11. Government Support Screening                              */
/* ============================================================ */

export function GovernmentSupportStep({ data, onChange }) {
  const g = data.government_support || {};
  const set = (patch) => onChange({ government_support: { ...g, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-government-support">
      <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-900">
        Sensitive and optional. Many regions offer free retraining grants and unemployment
        support — answering helps us flag what you might qualify for.
      </div>
      <Field label="Country (for benefits & programs)" optional>
        <TextInput
          value={g.country}
          onChange={(v) => set({ country: v })}
          placeholder="e.g. United Kingdom"
          testid="input-gov-country"
        />
      </Field>
      <Field label="Currently receiving unemployment benefits?" sensitive>
        <TriToggle
          value={g.receiving_unemployment_benefits}
          onChange={(v) => set({ receiving_unemployment_benefits: v })}
          testid="input-receiving-unemployment"
        />
      </Field>
      <Field label="Eligible for retraining grants?" sensitive>
        <TriToggle
          value={g.eligible_for_retraining_grants}
          onChange={(v) => set({ eligible_for_retraining_grants: v })}
          testid="input-eligible-retraining-grants"
        />
      </Field>
      <Field label="Have you explored local programs?" optional>
        <TriToggle
          value={g.has_explored_local_programs}
          onChange={(v) => set({ has_explored_local_programs: v })}
          testid="input-explored-local-programs"
        />
      </Field>
      <Field label="Notes about programs you know of" optional>
        <TextArea
          value={g.program_notes}
          onChange={(v) => set({ program_notes: v })}
          rows={3}
          testid="input-program-notes"
        />
      </Field>
    </div>
  );
}

/* ============================================================ */
/* 12. Career Goals                                              */
/* ============================================================ */

export function CareerGoalsStep({ data, onChange, errors = {} }) {
  const g = data.career_goals || {};
  const set = (patch) => onChange({ career_goals: { ...g, ...patch } });

  return (
    <div className="space-y-6" data-testid="step-career-goals">
      <Field
        label="In the next 3–6 months, what would 'progress' look like?"
        error={errors.short_term_3_6_months}
        hint="One or two sentences is fine."
      >
        <TextArea
          value={g.short_term_3_6_months}
          onChange={(v) => set({ short_term_3_6_months: v })}
          rows={3}
          placeholder="e.g. land steady part-time work in editing or comms"
          testid="input-short-term"
        />
      </Field>
      <Field label="In 1–2 years?" optional>
        <TextArea
          value={g.medium_term_1_2_years}
          onChange={(v) => set({ medium_term_1_2_years: v })}
          rows={3}
          testid="input-medium-term"
        />
      </Field>
      <Field label="In 5 years?" optional>
        <TextArea
          value={g.long_term_5_years}
          onChange={(v) => set({ long_term_5_years: v })}
          rows={3}
          testid="input-long-term"
        />
      </Field>
      <Field
        label="Must-haves in your next role"
        optional
        hint="The things that are non-negotiable for you."
      >
        <TagInput
          value={g.must_haves}
          onChange={(v) => set({ must_haves: v })}
          placeholder="e.g. remote, flexible hours"
          testid="input-must-haves"
        />
      </Field>
      <Field
        label="Deal-breakers"
        optional
        hint="What would make you say no immediately?"
      >
        <TagInput
          value={g.deal_breakers}
          onChange={(v) => set({ deal_breakers: v })}
          placeholder="e.g. night shifts, commute > 1h"
          testid="input-deal-breakers"
        />
      </Field>
    </div>
  );
}

/* ============================================================ */
/* Steps registry                                                */
/* ============================================================ */

export const STEPS = [
  {
    key: "current_situation",
    title: "Current Situation",
    subtitle: "Where things stand today.",
    Component: CurrentSituationStep,
    sections: ["current_situation"],
    validate: (data) => {
      const cs = data.current_situation || {};
      const errors = {};
      if (!cs.employment_status) {
        errors.employment_status = "Please pick your current employment status.";
      }
      return errors;
    },
  },
  {
    key: "work_history_skills",
    title: "Work History & Skills",
    subtitle: "What you've done and what you do well.",
    Component: WorkHistorySkillsStep,
    sections: ["work_history", "skills"],
  },
  {
    key: "education",
    title: "Education",
    subtitle: "Anything from school to certificates.",
    Component: EducationStep,
    sections: ["education"],
  },
  {
    key: "interests_hobbies",
    title: "Interests & Hobbies",
    subtitle: "What you enjoy when no one's paying you.",
    Component: InterestsHobbiesStep,
    sections: ["hobbies", "career_goals"],
  },
  {
    key: "personality",
    title: "Personality & Work Style",
    subtitle: "How you work best.",
    Component: PersonalityStep,
    sections: ["personality"],
  },
  {
    key: "physical_constraints",
    title: "Physical & Personal Constraints",
    subtitle: "Optional and sensitive — share only what you want.",
    Component: PhysicalConstraintsStep,
    sections: ["physical_constraints"],
  },
  {
    key: "salary",
    title: "Salary & Income",
    subtitle: "What you need to earn.",
    Component: SalaryStep,
    sections: ["salary"],
  },
  {
    key: "financial_position",
    title: "Financial Position",
    subtitle: "Optional — helps us calibrate timing.",
    Component: FinancialPositionStep,
    sections: ["financial_position"],
  },
  {
    key: "location",
    title: "Location, Travel & Relocation",
    subtitle: "Where and how you can work.",
    Component: LocationStep,
    sections: ["location_travel_relocation"],
  },
  {
    key: "retraining",
    title: "Retraining Capacity",
    subtitle: "How much time and money you can put into learning.",
    Component: RetrainingStep,
    sections: ["retraining_capacity"],
  },
  {
    key: "government_support",
    title: "Government Support Screening",
    subtitle: "What you might qualify for.",
    Component: GovernmentSupportStep,
    sections: ["government_support"],
  },
  {
    key: "career_goals",
    title: "Career Goals",
    subtitle: "What you want next.",
    Component: CareerGoalsStep,
    sections: ["career_goals"],
    validate: (data) => {
      const g = data.career_goals || {};
      const errors = {};
      if (!g.short_term_3_6_months || g.short_term_3_6_months.trim().length < 4) {
        errors.short_term_3_6_months = "Tell us what progress would look like in the next 3–6 months.";
      }
      return errors;
    },
  },
];
